import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Moon, Sun, Download } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const navItems = [
  { id: 'hero', label: 'about' },
  { id: 'skills', label: 'skills' },
  { id: 'projects', label: 'projects' },
  { id: 'playground', label: 'playground' },
  { id: 'experience', label: 'experience' },
];

export const Navbar: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setIsOpen(false);
    if (window.location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const el = document.getElementById(sectionId);
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background/90 backdrop-blur-md border-b border-border' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
        <a
          href="/"
          className="font-mono text-sm sm:text-base text-text hover:text-accent transition-colors whitespace-nowrap shrink-0"
        >
          <span className="text-accent">aditya</span>
          <span className="text-muted">@</span>
          <span>portfolio</span>
          <span className="text-muted">:~$</span>
          <span className="inline-block w-2 h-4 bg-accent ml-1 align-middle animate-blink" />
        </a>

        <div className="hidden lg:flex items-center gap-1 font-mono text-sm">
          {navItems.map((item, i) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="group px-2.5 py-2 text-muted hover:text-text transition-colors whitespace-nowrap"
            >
              <span className="text-accent/70 group-hover:text-accent">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="mx-1 text-border">/</span>
              {item.label}
            </button>
          ))}
          <a
            href="/#contact"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = '/contact';
            }}
            className="ml-2 px-4 py-2 border border-accent/40 text-accent rounded hover:bg-accent/10 transition-colors whitespace-nowrap"
          >
            contact()
          </a>
        </div>

        <div className="flex items-center gap-2">
          <a
            href="/Aditya_Bharti_Data_Analyst.pdf"
            download
            className="hidden xl:flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono text-muted hover:text-accent border border-border rounded transition-colors whitespace-nowrap"
            title="Download resume"
          >
            <Download size={13} />
            resume.pdf
          </a>
          <button
            onClick={toggleTheme}
            className="p-2 text-muted hover:text-accent transition-colors"
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button
            className="lg:hidden p-2 text-text"
            onClick={() => setIsOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-surface border-b border-border"
          >
            <div className="px-6 py-4 flex flex-col gap-1 font-mono text-sm">
              {navItems.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left py-2 text-muted hover:text-accent transition-colors"
                >
                  <span className="text-accent/70">{String(i + 1).padStart(2, '0')}</span>
                  <span className="mx-1 text-border">/</span>
                  {item.label}
                </button>
              ))}
              <a href="/Aditya_Bharti_Data_Analyst.pdf" download className="py-2 text-muted hover:text-accent transition-colors">
                <span className="text-accent/70">--</span>
                <span className="mx-1 text-border">/</span>
                resume.pdf
              </a>
              <a
                href="/contact"
                className="py-2 text-accent"
              >
                contact()
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
